import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Heart, Baby, Briefcase, Clock, DollarSign,
  CheckCircle, AlertCircle, ChevronDown, ChevronUp, Zap,
  Calendar, Users, ArrowRight, Star
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const MeiInssPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const navigateTo = (url) => {
    window.location.href = url;
  };

  // Benefícios do INSS para MEI
  const beneficios = [
    {
      icon: Briefcase,
      titulo: 'Aposentadoria por Idade',
      descricao: 'Homem: 65 anos + 20 anos de contribuição. Mulher: 62 anos + 15 anos de contribuição.',
      valor: '1 salário mínimo',
      carencia: '180 meses (15 anos)',
      cor: 'blue'
    },
    {
      icon: Heart,
      titulo: 'Aposentadoria por Invalidez',
      descricao: 'Quando você fica permanentemente incapaz de trabalhar por doença ou acidente.',
      valor: '1 salário mínimo',
      carencia: '12 meses',
      cor: 'purple'
    },
    {
      icon: Shield,
      titulo: 'Auxílio-Doença',
      descricao: 'Quando você fica temporariamente incapaz de trabalhar por mais de 15 dias.',
      valor: '1 salário mínimo',
      carencia: '12 meses',
      cor: 'emerald'
    },
    {
      icon: Baby,
      titulo: 'Salário-Maternidade',
      descricao: 'Para gestantes, adotantes ou em caso de aborto não criminoso. 120 dias de benefício.',
      valor: '1 salário mínimo',
      carencia: '10 meses',
      cor: 'pink'
    },
    {
      icon: Users,
      titulo: 'Pensão por Morte',
      descricao: 'Para dependentes em caso de falecimento do MEI. Cônjuge, filhos menores, etc.',
      valor: '1 salário mínimo',
      carencia: '24 meses para cônjuge',
      cor: 'amber'
    },
    {
      icon: Shield,
      titulo: 'Auxílio-Reclusão',
      descricao: 'Para dependentes caso o MEI seja preso em regime fechado ou semiaberto.',
      valor: '1 salário mínimo',
      carencia: '24 meses',
      cor: 'gray'
    },
  ];

  // Como funciona a contribuição
  const contribuicao = {
    aliquota: '5%',
    base: 'Salário Mínimo',
    valorMensal: 'R$ 70,60',
    obs: 'Já está incluso no DAS mensal'
  };

  // FAQ
  const faqs = [
    {
      pergunta: 'MEI tem direito a aposentadoria?',
      resposta: 'Sim! O MEI contribui com 5% do salário mínimo para o INSS através do DAS mensal e tem direito à aposentadoria por idade. A aposentadoria é de 1 salário mínimo. Homens precisam ter 65 anos + 20 anos de contribuição, mulheres 62 anos + 15 anos de contribuição.'
    },
    {
      pergunta: 'O tempo como MEI conta para aposentadoria?',
      resposta: 'Sim! Todo mês que você paga o DAS em dia conta como tempo de contribuição para o INSS. Se você já contribuiu como CLT antes, esse tempo pode ser somado ao tempo como MEI.'
    },
    {
      pergunta: 'MEI pode se aposentar por tempo de contribuição?',
      resposta: 'Não. A contribuição do MEI (5%) dá direito apenas à aposentadoria por idade. Para ter direito à aposentadoria por tempo de contribuição, você precisa complementar a contribuição pagando mais 15% sobre o salário mínimo (guia complementar).'
    },
    {
      pergunta: 'MEI gestante tem direito a salário-maternidade?',
      resposta: 'Sim! A MEI gestante tem direito a 120 dias (4 meses) de salário-maternidade no valor de 1 salário mínimo. A carência é de 10 meses de contribuição. O benefício também vale para adoção.'
    },
    {
      pergunta: 'Como solicitar benefício do INSS sendo MEI?',
      resposta: 'Você pode solicitar pelo app Meu INSS, pelo site meu.inss.gov.br ou pelo telefone 135. Tenha em mãos: documentos pessoais, número do NIT/PIS, comprovantes de pagamento do DAS e documentação específica do benefício.'
    },
    {
      pergunta: 'Se eu atrasar o DAS, perco os direitos do INSS?',
      resposta: 'Os meses em atraso não contam como tempo de contribuição até serem pagos. Se você está em período de carência e atrasar, pode perder o direito ao benefício. Mantenha o DAS sempre em dia!'
    },
    {
      pergunta: 'MEI que também é CLT contribui duas vezes?',
      resposta: 'Sim, você contribui como MEI (DAS) e como CLT (desconto em folha). Porém, o valor do benefício será baseado na soma das contribuições, limitado ao teto do INSS. É vantajoso para aumentar o valor da aposentadoria.'
    },
    {
      pergunta: 'O que é a guia complementar do INSS?',
      resposta: 'É uma guia adicional de 15% sobre o salário mínimo que você pode pagar se quiser direito à aposentadoria por tempo de contribuição (não apenas por idade). Também permite usar o tempo como MEI para se aposentar com valor maior que 1 salário mínimo.'
    },
  ];

  // Requisitos por benefício
  const requisitosDetalhados = [
    {
      beneficio: 'Aposentadoria por Idade',
      requisitos: [
        'Homem: 65 anos de idade',
        'Mulher: 62 anos de idade',
        'Tempo mínimo: 15 anos (mulher) ou 20 anos (homem) de contribuição',
        'DAS pago regularmente'
      ]
    },
    {
      beneficio: 'Auxílio-Doença',
      requisitos: [
        'Mínimo 12 meses de contribuição',
        'Incapacidade temporária comprovada',
        'Perícia médica do INSS',
        'Estar afastado há mais de 15 dias'
      ]
    },
    {
      beneficio: 'Salário-Maternidade',
      requisitos: [
        'Mínimo 10 meses de contribuição',
        'Ser gestante, adotante ou guarda judicial',
        'Solicitar até 28 dias antes ou 92 dias após o parto'
      ]
    },
  ];

  return (
    <div className="min-h-screen font-sans bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "MEI e INSS: Aposentadoria e Benefícios Previdenciários 2025",
          "description": "Entenda todos os direitos do MEI no INSS: aposentadoria, auxílio-doença, salário-maternidade, pensão por morte e muito mais.",
          "author": { "@type": "Organization", "name": "MeuMei" }
        })
      }} />

      <Header />

      {/* Hero */}
      <section 
        className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-4 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' }}
      >
        <div className="max-w-4xl mx-auto relative z-10">
          <nav className="text-white/70 text-sm mb-6">
            <a href="/" className="hover:text-white">Início</a>
            <span className="mx-2">›</span>
            <a href="/blog" className="hover:text-white">Blog</a>
            <span className="mx-2">›</span>
            <span className="text-white">MEI e INSS</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <Shield className="w-4 h-4" />
              Seus Direitos
            </motion.span>

            <h1 className="text-3xl md:text-5xl font-bold mb-6">
              MEI e <span className="text-yellow-300">INSS</span><br />
              Aposentadoria e Benefícios
            </h1>
            
            <p className="text-lg md:text-xl max-w-2xl mx-auto opacity-90">
              Tudo sobre os direitos previdenciários do MEI: aposentadoria, 
              auxílio-doença, salário-maternidade e muito mais.
            </p>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* CTA Mobile */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg z-40 md:hidden">
        <motion.button
          onClick={() => navigateTo('https://app.meumei.net/register')}
          whileTap={{ scale: 0.98 }}
          className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold rounded-xl"
        >
          Manter DAS em Dia com MeuMei
        </motion.button>
      </div>

      {/* Conteúdo */}
      <article className="py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Intro */}
          <section className="mb-16">
            <p className="text-xl text-gray-600 leading-relaxed">
              O <strong>MEI contribui para o INSS</strong> através do DAS mensal, pagando apenas 
              <strong> 5% do salário mínimo</strong> (R$ 70,60 em 2025). Com isso, você tem direito 
              a diversos <strong>benefícios previdenciários</strong>. Veja quais são e como funciona:
            </p>

            {/* Box contribuição */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6 md:p-8"
            >
              <h3 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Sua contribuição ao INSS
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-white rounded-xl p-4">
                  <p className="text-sm text-gray-500">Alíquota</p>
                  <p className="text-2xl font-bold text-blue-600">5%</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <p className="text-sm text-gray-500">Base</p>
                  <p className="text-lg font-bold text-blue-600">Salário Mínimo</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <p className="text-sm text-gray-500">Valor 2025</p>
                  <p className="text-2xl font-bold text-blue-600">R$ 70,60</p>
                </div>
                <div className="bg-white rounded-xl p-4">
                  <p className="text-sm text-gray-500">Pagamento</p>
                  <p className="text-lg font-bold text-blue-600">Incluso no DAS</p>
                </div>
              </div>
            </motion.div>
          </section>

          {/* Benefícios */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8">
              🛡️ Benefícios do INSS para MEI
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {beneficios.map((beneficio, index) => (
                <motion.div
                  key={beneficio.titulo}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    beneficio.cor === 'blue' ? 'bg-blue-100' :
                    beneficio.cor === 'purple' ? 'bg-purple-100' :
                    beneficio.cor === 'emerald' ? 'bg-emerald-100' :
                    beneficio.cor === 'pink' ? 'bg-pink-100' :
                    beneficio.cor === 'amber' ? 'bg-amber-100' : 'bg-gray-100'
                  }`}>
                    <beneficio.icon className={`w-6 h-6 ${
                      beneficio.cor === 'blue' ? 'text-blue-600' :
                      beneficio.cor === 'purple' ? 'text-purple-600' :
                      beneficio.cor === 'emerald' ? 'text-emerald-600' :
                      beneficio.cor === 'pink' ? 'text-pink-600' :
                      beneficio.cor === 'amber' ? 'text-amber-600' : 'text-gray-600'
                    }`} />
                  </div>
                  <h3 className="font-bold text-gray-800 text-lg mb-2">{beneficio.titulo}</h3>
                  <p className="text-gray-600 text-sm mb-4">{beneficio.descricao}</p>
                  <div className="flex gap-4 text-xs">
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      💰 {beneficio.valor}
                    </span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      ⏱️ Carência: {beneficio.carencia}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Alerta importante */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-amber-50 border border-amber-300 rounded-2xl p-6"
            >
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-amber-800 mb-2">⚠️ Importante: Mantenha o DAS em dia!</h3>
                  <p className="text-amber-700">
                    Para ter direito aos benefícios, você precisa estar com o DAS <strong>pago regularmente</strong>. 
                    Meses em atraso não contam para carência. Use o MeuMei para receber lembretes automáticos 
                    e nunca perder seus direitos!
                  </p>
                </div>
              </div>
            </motion.div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              ❓ Perguntas Frequentes
            </h2>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="border rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50"
                  >
                    <span className="font-semibold text-gray-800 pr-4">{faq.pergunta}</span>
                    {openFaq === index ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-6 text-gray-600 leading-relaxed">{faq.resposta}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Final */}
          <section>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-3xl p-8 md:p-12 text-center text-white"
            >
              <h2 className="text-2xl md:text-4xl font-bold mb-4">
                Garanta seus direitos no INSS!
              </h2>
              <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
                Mantenha seu DAS sempre em dia com lembretes automáticos. 
                Com o MeuMei você nunca mais esquece de pagar!
              </p>
              <motion.button
                onClick={() => navigateTo('https://app.meumei.net/register')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white text-blue-600 font-bold rounded-xl shadow-lg"
              >
                Começar Grátis - 7 Dias Free
              </motion.button>
            </motion.div>
          </section>

        </div>
      </article>

      <div className="h-24 md:hidden" />
      <Footer />
    </div>
  );
};

export default MeiInssPage;